import React, {Component} from "react"
import PropTypes from "prop-types"
import moment from "moment"

import {Body, H1, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import adminTracking from "@irati-tracking/admin"
import OnboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import authentication from "@irati/auth"
import History from "@irati/utils/history"
import Selector from "@irati/ui/src/Selector"

import globalImg from "../../../images/global.png"
import nextStepImg from "../../../images/next_step_global.png"
import globalModalGif from "../../../images/global_modal.gif"
import {StyledImg} from "./styled"
import {VIEW_BUSINESS_UNITS} from "../../../routes/routeConfig"
import {SelectorWrapper} from "../Integrations/styled"

interface ReasonsInterface {
  key: string,
  reason: string
}

const REASONS: ReasonsInterface[] = [
  {
    key: "admin.dashboard.globalModal.pill.noGlobalEmployees",
    reason: "admin_dashboard_globalModal_clickNoGlobalEmployees"
  },
  {
    key: "admin.dashboard.globalModal.pill.needApprovals",
    reason: "admin_dashboard_globalModal_clickNeedApprovals"
  },
  {
    key: "admin.dashboard.globalModal.pill.moreQuestions",
    reason: "admin_dashboard_globalModal_clickMoreQuestions"
  },
  {
    key: "admin.dashboard.globalModal.pill.notNow",
    reason: "admin_dashboard_globalModal_clickMaybeLater"
  },
  {
    key: "admin.dashboard.globalModal.pill.alreadyAdded",
    reason: "admin_dashboard_globalModal_clickAddedGlobalAlready"
  },
  {
    key: "admin.dashboard.globalModal.pill.needForGlobalTeams",
    reason: "admin_dashboard_globalModal_clickDoNotSeeNeed"
  },
  {
    key: "admin.dashboard.globalModal.pill.other",
    reason: "admin_dashboard_globalModal_clickOther"
  }
]

interface Props extends InjectIntl{}

interface State {
  showModal: boolean,
  showReasonsModal: boolean,
  nextStep: boolean,
  finalStep: boolean,
  selectedReasons: string[]
}

class GlobalModal extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }
  state: State = {
    showModal: false,
    showReasonsModal: false,
    nextStep: false,
    finalStep: false,
    selectedReasons: []
  }

  componentDidMount() {
    OnboardingService.get(authentication.user().uuid).then((res) => {
      const dismissedStep = res.userProgress?.find(((item) => item.step === OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL))
      const completedStep = res.userProgress?.find(((item) => item.step === OnboardingStep.HAS_COMPLETED_GLOBAL_MODAL))

      if(!completedStep) {
        if(!dismissedStep) {
          return this.setState({showModal: true})
        }
        const dismissedDate = moment(dismissedStep.metadata?.createdAt, "YYYY-MM-DD")
        const currentDate = moment().startOf("day")
        if (moment.duration(currentDate.diff(dismissedDate)).asDays() > 14) {
          this.setState({showModal: true})
        }
      }
    })
  }

  addCountries = () => {
    OnboardingService.removeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
      OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
        if (this.state.nextStep) {
          adminTracking.globalModalWhatIsThisAddCountries()
        } else {
          adminTracking.globalModalAddCountries()
        }
        History.push(VIEW_BUSINESS_UNITS, this.context.apps.ADMIN)
      })
    })
  }

  nextStep = () => {
    if (this.state.nextStep) {
      adminTracking.globalModalWhatIsThis()
      this.setState({finalStep: true, nextStep: false})
    } else {
      adminTracking.globalModalWhatIsThisNotRelevant()
      this.setState({nextStep: true})
    }
  }

  handleCloseFinalStep = () => {
    OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_COMPLETED_GLOBAL_MODAL).then(() => {
      adminTracking.globalModalNotRelevantConfirm()
      this.setState({showModal: false, showReasonsModal: true})
    })
  }

  handleClose = () => {
    OnboardingService.removeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
      OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
        adminTracking.globalModalClose()
        this.setState({showModal: false, showReasonsModal: true})
      })
    })
  }

  handlePillClick = (_, selectedReason: string) => {
    const reasonEvent = REASONS.find((reason) => this.props.formatMessage({id: reason.key}) === selectedReason)?.reason
    if (reasonEvent) {
      const {selectedReasons} = this.state
      let updatedReasons
      if (selectedReasons.find((reason) => reason === reasonEvent)) {
        updatedReasons = selectedReasons.filter((reason) => reason !== reasonEvent)
      } else {
        updatedReasons = [...selectedReasons, reasonEvent]
      }
      this.setState({selectedReasons: updatedReasons})
    }
  }

  handleSubmitReasons = () => {
    const {selectedReasons} = this.state
    OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_COMPLETED_GLOBAL_MODAL).then(() => {
      adminTracking.submitGlobalModalReasons(selectedReasons)
      this.setState({showReasonsModal: false})
    })
  }

  handleCloseReasons = () => {
    OnboardingService.removeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
      OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_DISMISSED_GLOBAL_MODAL).then(() => {
        adminTracking.globalModalClose()
        this.setState({showReasonsModal: false})
      })
    })
  }

  render() {
    const {showModal, nextStep, finalStep, showReasonsModal, selectedReasons} = this.state
    const {formatMessage} = this.props

    const title = nextStep ? "admin.containers.globalModal.nextStepTitle" : "admin.containers.globalModal.title"
    const text = nextStep ? "admin.containers.globalModal.nextStepText" : "admin.containers.globalModal.text"
    const image = nextStep ? nextStepImg : globalImg
    const button = nextStep ? "admin.containers.globalModal.notRelevantButton" : "admin.containers.globalModal.button.whatIsThis"

    return (
      <>
        <GenericModal
          customWidth={1100}
          show={showModal}
          hideActions
          onHide={this.handleClose}>
          {!finalStep && <Flex height="500px" alignItems="center">
            <div>
              <H1>
                <FormattedMessage id={title} />
              </H1>
              <Body marginTop={2}>
                <FormattedMessage id={text} />
              </Body>
              <Button primary onClick={this.addCountries} marginRight={1} marginTop={3}>
                <FormattedMessage id="admin.containers.globalModal.button.addCountries" />
              </Button>
              <Button onClick={this.nextStep} marginTop={3}>
                <FormattedMessage id={button} />
              </Button>
            </div>
            <StyledImg src={image} alt="Global" />
          </Flex>}
          {finalStep && <Flex height="500px" alignItems="center">
            <div>
              <H1>
                <FormattedMessage id="admin.containers.globalModal.finalStepTitle" />
              </H1>
              <Body marginTop={2}>
                <FormattedMessage id="admin.containers.globalModal.finalStepText" />
              </Body>
              <Button primary onClick={this.handleCloseFinalStep} marginTop={3}>
                <FormattedMessage id="admin.containers.globalModal.finalStepButton" />
              </Button>
            </div>
            <StyledImg isGif src={globalModalGif} alt="GlobalGif" />
          </Flex>}
        </GenericModal>
        <GenericModal
          customWidth={800}
          show={showReasonsModal}
          hideActions
          onHide={this.handleCloseReasons}>
          <H2 marginBottom={1}>
            <FormattedMessage id="admin.dashboard.integrations.feedback.title" />
          </H2>
          <Body marginBottom={4}>
            <FormattedMessage id="admin.dashboard.integrations.feedback.text" />
          </Body>
          <SelectorWrapper>
            {REASONS.map((reason: ReasonsInterface) => <Selector
              name={formatMessage({id: reason.key})}
              onClick={this.handlePillClick}
              selected={selectedReasons.includes(reason.reason)} />
            )}
          </SelectorWrapper>
          <Button marginTop={4} primary onClick={this.handleSubmitReasons}>
            <FormattedMessage id="admin.dashboard.integrations.feedback.button.confirm" />
          </Button>
          <Button marginLeft onClick={this.handleCloseReasons}>
            <FormattedMessage id="admin.dashboard.integrations.feedback.button.cancel" />
          </Button>
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(GlobalModal)
